import { GetCompanyMaterialityMatrixResponse } from '@upright/common/model/output/materiality/materiality-matrix/api-types';
import {
  isValidWeightSetResultParams,
  isValidVariantParams,
  RenderViewCsrdMaterialityMatrixParams,
} from '@upright/common/renderer/component-types/types-render-view-csrd-matriality-matrix';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import { fetchCompanyMaterialityMatrixData } from '@upright/ui/api/model/output/scores/materiality/company';
import { fetchWeightSetMaterialityMatrixData } from '@upright/ui/api/model/output/scores/materiality/weight-set-csrd';
import { MaterialityMatrix } from '@upright/ui/common/upright/model/output/scores/csrd/materiality-matrix';
import { TopicMatterMatrix } from '@upright/ui/common/upright/model/output/scores/csrd/csrd-company-double-materiality/overview/matrix/topic-matter-matrix';
import { isCompanyVariantMatrixData } from '@upright/common/model/output/materiality/materiality-matrix/types';
import { leena2VersionMarkerSuffix } from '@upright/ui/common/upright/model/output/leena2-version-marker';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useRenderViewContext } from '../../context';

export const RenderViewCsrdMaterialityMatrix = (props: { params: RenderViewCsrdMaterialityMatrixParams }) => {
  const { params } = props;
  const context = useRenderViewContext();

  const query = useGetMatrixData(params);

  if (query.isError || query.isPending) {
    return null;
  }

  // A company's matrix exports in the design it is shown in, the view and topic filter included;
  // portfolio and group matrices keep the matter matrix they are shown with.
  if (isValidVariantParams(params)) {
    const { entityData } = query.data;
    return (
      <CompanyRoot style={{ width: context.options.width }}>
        <TopicMatterMatrix
          data={query.data}
          view={params.view ?? 'topics'}
          selectedTopicIds={params.topicIds ?? []}
          entityName={params.entityName}
          isStatic
        />
        <VersionInfo>
          Release {entityData.release}
          {isCompanyVariantMatrixData(entityData) && (
            <>
              {' · '}Version {entityData.productDataVersion}
              {leena2VersionMarkerSuffix(entityData.productDataVersion)}
            </>
          )}
        </VersionInfo>
      </CompanyRoot>
    );
  }

  return (
    <Root style={{ width: context.options.width }}>
      <MaterialityMatrix
        data={query.data}
        entityName={params.entityName}
        imageExportParams={undefined}
        renderOptions={{ hideRenderViewButton: true, showVersionInfo: true }}
      />
    </Root>
  );
};

const Root = styled.div``;

const CompanyRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
  padding: var(--space__xl);
  background: var(--color__white);
`;

const VersionInfo = styled.div`
  font-size: var(--font-size__sm);
  line-height: var(--line-height__xs);
  color: var(--color__gray-600);
`;

function useGetMatrixData(params: RenderViewCsrdMaterialityMatrixParams) {
  const query = useQuery<GetCompanyMaterialityMatrixResponse, ErrorWithHttpStatus>({
    queryKey: ['render-view-csrd-materiality-matrix', params],
    queryFn: async () => {
      if (isValidVariantParams(params)) {
        return await fetchCompanyMaterialityMatrixData({ variantId: params.variantId }, {});
      }
      if (isValidWeightSetResultParams(params)) {
        return await fetchWeightSetMaterialityMatrixData(params.weightSetResultId);
      }
      throw Error('Invalid params');
    },
  });
  return query;
}
